import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

/**
 * Editor for the scrolling notice strip under the homepage hero.
 *
 * Deliberately small: a switch, a speed, and a list of lines. The lines are
 * shown as plain text on the site — not clickable — so there is no link field.
 *
 * The whole blob is replaced on save, so a failed load has to block saving —
 * otherwise an empty screen would overwrite the school's notices with nothing.
 */

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const addBtnCls =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const delBtnCls = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";
const moveBtnCls = "bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs";

export default function MarqueePage() {
  const [enabled, setEnabled] = useState(true);
  const [speed, setSpeed] = useState(30);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/marquee")) || {};
      setEnabled(d.enabled !== false);
      setSpeed(Number(d.speed) > 0 ? Number(d.speed) : 30);
      setItems(d.items || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upd = (i, field, value) =>
    setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));

  const move = (i, dir) =>
    setItems((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const copy = [...arr];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const payload = {
        enabled,
        speed: Number(speed) > 0 ? Number(speed) : 30,
        // A blank line would scroll past as an invisible gap.
        items: items
          .filter((i) => (i.text || "").trim())
          .map((i) => ({ text: i.text.trim(), href: (i.href || "").trim() || undefined })),
      };
      const saved = await adminPut("/admin/content/marquee", payload);
      if (saved) {
        setEnabled(saved.enabled !== false);
        setSpeed(Number(saved.speed) > 0 ? Number(saved.speed) : 30);
        setItems(saved.items || []);
      }
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Notice ticker"
          action={
            <button
              type="button"
              className={addBtnCls}
              onClick={() => setItems((arr) => [...arr, { text: "" }])}
            >
              + Add line
            </button>
          }
        />
        <div className="p-[22px]">
          <p className="text-muted text-[12.5px] mb-4">
            The scrolling strip under the homepage hero. Lines run in this order and are shown
            as plain text — they are not clickable. With no lines, or with the strip switched
            off, nothing is shown on the site at all.
          </p>

          <div className="flex flex-wrap items-center gap-6 mb-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-[#005C40]"
              />
              <span className="text-sm font-semibold">Show the ticker</span>
            </label>

            <label className="flex items-center gap-2">
              <span className="text-[12px] font-semibold text-muted">Seconds per loop</span>
              <input
                type="number"
                min="5"
                max="180"
                value={speed}
                onChange={(e) => setSpeed(e.target.value)}
                className={`${inputCls} w-[90px]`}
              />
              <span className="text-[12px] text-muted">higher is slower</span>
            </label>
          </div>

          {items.length === 0 && <p className="text-muted text-sm italic">No lines yet.</p>}

          {items.map((item, i) => (
            <div key={i} className="flex gap-3 items-start border border-line rounded-lg p-3 mb-2">
              {/* No link field: the site renders these as plain text for now.
                  Any href already saved is carried through untouched. */}
              <div className="flex-1 min-w-0">
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Text</span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={item.text || ""}
                    onChange={(e) => upd(i, "text", e.target.value)}
                    placeholder="একাদশ শ্রেণিতে ভর্তি চলছে"
                  />
                </label>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button type="button" className={moveBtnCls} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button type="button" className={moveBtnCls} onClick={() => move(i, 1)}>
                  ↓
                </button>
                <button
                  type="button"
                  className={delBtnCls}
                  onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-lg px-5 py-2.5 font-sans font-semibold text-sm cursor-pointer disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {status && <span className="text-[13px] text-green font-semibold">{status}</span>}
      </div>
    </div>
  );
}
